import React, { useEffect, useState } from "react";
import styles from "../Followers.module.scss";
import { api } from "../../../services/api";
import { PublicUser } from "../../../types/social";
import UserCard from "../../../components/UserCard";
import { useUser } from "../../../context/UserContext";
import { SkeletonContainer } from "../../../components/Skeleton";
import { FollowersTabSkeleton } from "./components/FollowersTabSkeleton";
import EmptyState from '../../../components/EmptyState';

interface FollowersProps {
  mode: "followers" | "following";
  userId?: string;
}

const Followers: React.FC<FollowersProps> = ({ mode, userId }) => {
  const [users, setUsers] = useState<PublicUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [followStates, setFollowStates] = useState<Record<string, boolean>>({});
  const { user: currentUser } = useUser();

  useEffect(() => {
    const fetchUsers = async () => {
      if (!userId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = mode === "followers"
          ? await api.social.getFollowers(userId)
          : await api.social.getFollowing(userId);

        setUsers(response.users);

        // Fetch follow status for each user
        if (currentUser?.userId) {
          const followStatusPromises = response.users.map(async (u) => {
            try {
              const status = await api.social.isFollowing(u.userId);
              return { userId: u.userId, isFollowing: status.isFollowing };
            } catch {
              return { userId: u.userId, isFollowing: false };
            }
          });

          const statuses = await Promise.all(followStatusPromises);
          const statesMap = statuses.reduce((acc, { userId, isFollowing }) => {
            acc[userId] = isFollowing;
            return acc;
          }, {} as Record<string, boolean>);

          setFollowStates(statesMap);
        }
      } catch (err) {
        console.error(`Failed to fetch ${mode}:`, err);
        setError(`Failed to load ${mode}`);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [mode, userId, currentUser?.userId]);

  return (
    <div className={styles.friendList}>
      <SkeletonContainer
        loading={loading}
        skeleton={<FollowersTabSkeleton />}
        label={mode}
      >
        {error ? (
          <div className={styles.error}>{error}</div>
        ) : users.length === 0 ? (
          <EmptyState heading={`No ${mode} yet.`} />
        ) : (
          <div className={styles.friendList}>
            {users.map((user) => (
              <UserCard
                key={user.userId}
                user={user}
                currentUserId={currentUser?.userId}
                initialIsFollowing={followStates[user.userId] ?? user.isFollowing ?? false}
                initialHasPendingRequest={user.hasPendingRequest ?? false}
                isTargetPrivate={user.isPrivate ?? false}
                onFollowChange={(isFollowing) =>
                  setFollowStates((prev) => ({ ...prev, [user.userId]: isFollowing }))
                }
              />
            ))}
          </div>
        )}
      </SkeletonContainer>
    </div>
  );
};

export default Followers;
